/**
 * Core Annotation Engine
 * Handles text selection, range serialization, and annotation rendering
 */

import type {
  Annotation,
  AnnotationTarget,
  AnnotationColor,
  AnnotationStyle,
} from "./types";
import { COLORS } from "./types";

const ANNOTATION_CLASS = "annotation-mark";
const ANNOTATION_ATTR = "data-annotation-id";

export class AnnotationEngine {
  private root: HTMLElement;
  private rendered: Map<string, HTMLElement[]> = new Map();

  constructor(root: HTMLElement = document.body) {
    this.root = root;
  }

  /**
   * Create annotation from current text selection
   */
  createFromSelection(): AnnotationTarget | null {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) return null;

    const range = selection.getRangeAt(0);
    const quote = range.toString().trim();

    // Validate selection
    if (!quote) return null;

    // Prevent excessively large selections (max 500 characters)
    if (quote.length > 500) {
      console.warn("Selection too large (max 500 characters)");
      alert("Please select a shorter text (max 500 characters)");
      return null;
    }

    // Check if selection is inside an existing annotation (prevent nesting)
    const node = range.commonAncestorContainer;
    let checkNode: HTMLElement | null =
      node.nodeType === Node.TEXT_NODE
        ? node.parentElement
        : (node as HTMLElement);

    while (checkNode && checkNode !== document.body) {
      if (checkNode.classList?.contains(ANNOTATION_CLASS)) {
        console.warn("Cannot annotate inside an existing annotation");
        alert(
          "Cannot create annotation inside another annotation. Please select text outside."
        );
        return null;
      }
      checkNode = checkNode.parentElement;
    }

    const container = this.findContainer(range.commonAncestorContainer);
    if (!container) {
      console.warn("Could not find suitable container for selection");
      return null;
    }

    // Get selector for container
    const selector = this.generateSelector(container);
    if (!selector || selector.trim() === "") {
      console.warn("Generated empty selector, cannot create annotation");
      return null;
    }

    // Calculate offsets relative to container
    const { startOffset, endOffset } = this.getOffsets(range, container);

    console.log("✓ Created annotation target:", {
      selector,
      quote: quote.substring(0, 50) + (quote.length > 50 ? "..." : ""),
      startOffset,
      endOffset,
    });

    return {
      selector,
      quote,
      startOffset,
      endOffset,
    };
  }

  /**
   * Render annotation on page
   */
  render(annotation: Annotation): boolean {
    try {
      // Validate selector
      if (
        !annotation.target.selector ||
        annotation.target.selector.trim() === ""
      ) {
        console.warn("Empty selector, skipping annotation:", annotation.id);
        return false;
      }

      const container = document.querySelector(
        annotation.target.selector
      ) as HTMLElement;
      if (!container) {
        console.warn(
          `Container not found for selector: ${annotation.target.selector}`,
          annotation
        );
        return false;
      }

      const range = this.createRange(annotation.target, container);
      if (!range) {
        console.warn("Failed to create range for annotation:", annotation.id);
        return false;
      }

      const elements = this.applyHighlight(range, annotation);
      if (elements.length > 0) {
        this.rendered.set(annotation.id, elements);
        console.log(
          `✓ Rendered annotation ${annotation.id} (${elements.length} elements)`
        );
        return true;
      } else {
        console.warn("No elements created for annotation:", annotation.id);
      }
    } catch (error) {
      console.error("Failed to render annotation:", annotation.id, error);
    }
    return false;
  }

  /**
   * Remove annotation from page
   */
  remove(annotationId: string): void {
    const elements = this.rendered.get(annotationId);
    if (!elements) return;

    elements.forEach((el) => {
      const parent = el.parentNode;
      if (!parent) return;

      // Move children out of wrapper
      while (el.firstChild) {
        parent.insertBefore(el.firstChild, el);
      }
      parent.removeChild(el);
    });

    // Normalize to merge adjacent text nodes
    elements.forEach((el) => el.parentNode?.normalize());

    this.rendered.delete(annotationId);
  }

  /**
   * Remove all annotations
   */
  removeAll(): void {
    for (const id of this.rendered.keys()) {
      this.remove(id);
    }
    this.rendered.clear();
  }

  /**
   * Clean up any malformed annotation marks (e.g., from previous versions)
   */
  cleanupMalformed(): void {
    // Find all annotation marks
    const marks = document.querySelectorAll(`.${ANNOTATION_CLASS}`);

    marks.forEach((mark) => {
      // Check if mark contains block-level elements (invalid structure)
      const hasBlockElements = mark.querySelector(
        "div, p, h1, h2, h3, h4, h5, h6, section, article, header, footer, main, nav, aside"
      );

      if (hasBlockElements) {
        // Remove this malformed mark
        const parent = mark.parentNode;
        if (parent) {
          while (mark.firstChild) {
            parent.insertBefore(mark.firstChild, mark);
          }
          parent.removeChild(mark);
          parent.normalize();
        }
      }
    });
  }

  /**
   * Find suitable container element for annotation
   */
  private findContainer(node: Node): HTMLElement | null {
    let current =
      node.nodeType === Node.TEXT_NODE
        ? node.parentElement
        : (node as HTMLElement);

    // Traverse up to find a good container
    while (current && current !== this.root) {
      // Prefer elements with IDs or specific attributes
      if (current.id || current.hasAttribute("data-annotatable")) {
        return current;
      }
      // Also accept semantic HTML elements as containers
      const tagName = current.tagName?.toLowerCase();
      if (
        tagName === "p" ||
        tagName === "div" ||
        tagName === "section" ||
        tagName === "article" ||
        tagName === "main" ||
        tagName === "li" ||
        tagName === "td" ||
        tagName === "th"
      ) {
        return current;
      }
      current = current.parentElement;
    }

    // Fallback to body if nothing better found
    return document.body;
  }

  /**
   * Generate CSS selector for element
   */
  private generateSelector(element: HTMLElement): string {
    // If element has an ID, use it (most reliable)
    if (element.id) {
      return `#${CSS.escape(element.id)}`;
    }

    // If element is body, return body selector
    if (element === document.body) {
      return "body";
    }

    const path: string[] = [];
    let current: HTMLElement | null = element;
    let maxDepth = 10; // Prevent infinite loops

    while (current && current !== document.body && maxDepth > 0) {
      let selector = current.tagName.toLowerCase();

      // Add classes (excluding annotation-related classes)
      if (current.className) {
        const classes = Array.from(current.classList)
          .filter(
            (c) => !c.startsWith("annotation-") && !c.startsWith("highlight-")
          )
          .slice(0, 3); // Limit to 3 classes for brevity
        if (classes.length > 0) {
          selector += "." + classes.map((c) => CSS.escape(c)).join(".");
        }
      }

      // Add nth-child for specificity
      const parent = current.parentElement;
      if (parent) {
        const siblings = Array.from(parent.children).filter(
          (s) => s.tagName === current!.tagName
        );
        if (siblings.length > 1) {
          const index = siblings.indexOf(current) + 1;
          selector += `:nth-of-type(${index})`;
        }
      }

      path.unshift(selector);
      current = current.parentElement;
      maxDepth--;
    }

    return path.length > 0 ? path.join(" > ") : "body";
  }

  /**
   * Get character offsets relative to container
   */
  private getOffsets(
    range: Range,
    container: HTMLElement
  ): { startOffset: number; endOffset: number } {
    const beforeRange = document.createRange();
    beforeRange.setStart(container, 0);
    beforeRange.setEnd(range.startContainer, range.startOffset);
    const startOffset = beforeRange.toString().length;
    const endOffset = startOffset + range.toString().length;

    return { startOffset, endOffset };
  }

  /**
   * Create Range from serialized target
   */
  private createRange(
    target: AnnotationTarget,
    container: HTMLElement
  ): Range | null {
    const textContent = container.textContent || "";

    // Verify quote exists
    const quoteIndex = textContent.indexOf(target.quote);
    if (quoteIndex === -1) {
      console.warn("Quote not found in container");
      return null;
    }

    try {
      const range = document.createRange();
      const treeWalker = document.createTreeWalker(
        container,
        NodeFilter.SHOW_TEXT
      );

      let currentOffset = 0;
      let startNode: Node | null = null;
      let startNodeOffset = 0;
      let endNode: Node | null = null;
      let endNodeOffset = 0;

      while (treeWalker.nextNode()) {
        const node = treeWalker.currentNode;
        const nodeLength = node.textContent?.length || 0;
        const nextOffset = currentOffset + nodeLength;

        if (!startNode && nextOffset >= target.startOffset) {
          startNode = node;
          startNodeOffset = target.startOffset - currentOffset;
        }

        if (nextOffset >= target.endOffset) {
          endNode = node;
          endNodeOffset = target.endOffset - currentOffset;
          break;
        }

        currentOffset = nextOffset;
      }

      if (!startNode || !endNode) {
        return null;
      }

      range.setStart(startNode, Math.max(0, startNodeOffset));
      range.setEnd(endNode, Math.max(0, endNodeOffset));

      return range;
    } catch (error) {
      console.error("Failed to create range:", error);
      return null;
    }
  }

  /**
   * Apply visual highlight to range
   */
  private applyHighlight(range: Range, annotation: Annotation): HTMLElement[] {
    const elements: HTMLElement[] = [];
    const { style, color, id } = annotation;
    const colorConfig = COLORS[color];

    try {
      // Get all text nodes in the range
      const textNodes = this.getTextNodesInRange(range);

      if (textNodes.length === 0) {
        console.warn("No text nodes found in range");
        return elements;
      }

      // Process nodes in reverse order to avoid offset issues from DOM modifications
      for (let i = textNodes.length - 1; i >= 0; i--) {
        const { node, startOffset, endOffset } = textNodes[i];

        try {
          // Validate node is still in document
          if (!node.parentNode || !document.contains(node)) {
            continue;
          }

          // Split text node if needed
          const textLength = node.textContent?.length || 0;
          let targetNode: Text = node;

          // Split at end first (to preserve offsets)
          if (endOffset < textLength) {
            targetNode.splitText(endOffset);
          }

          // Split at start
          if (startOffset > 0) {
            targetNode = targetNode.splitText(startOffset) as Text;
          }

          // Validate we have content to wrap
          if (!targetNode.textContent || targetNode.textContent.length === 0) {
            continue;
          }

          // Create wrapper for this text node
          const wrapper = document.createElement("mark");
          wrapper.className = ANNOTATION_CLASS;
          wrapper.setAttribute(ANNOTATION_ATTR, id);

          // Apply styles
          this.applyStyle(wrapper, style, color, colorConfig);

          // Wrap the text node
          const parent = targetNode.parentNode;
          if (parent) {
            parent.insertBefore(wrapper, targetNode);
            wrapper.appendChild(targetNode);
            elements.push(wrapper);
          }
        } catch (err) {
          console.warn("Failed to wrap text node:", err);
        }
      }
    } catch (error) {
      console.error("Failed to apply highlight:", error);
    }

    return elements;
  }

  /**
   * Get all text nodes within a range with their offsets
   */
  private getTextNodesInRange(range: Range): Array<{
    node: Text;
    startOffset: number;
    endOffset: number;
  }> {
    const textNodes: Array<{
      node: Text;
      startOffset: number;
      endOffset: number;
    }> = [];

    // Handle simple case: range starts and ends in the same text node
    if (
      range.startContainer === range.endContainer &&
      range.startContainer.nodeType === Node.TEXT_NODE
    ) {
      textNodes.push({
        node: range.startContainer as Text,
        startOffset: range.startOffset,
        endOffset: range.endOffset,
      });
      console.log(`Found ${textNodes.length} text node(s) (simple case)`);
      return textNodes;
    }

    // Complex case: range spans multiple nodes
    const treeWalker = document.createTreeWalker(
      range.commonAncestorContainer,
      NodeFilter.SHOW_TEXT,
      null
    );

    let currentNode: Node | null;
    while ((currentNode = treeWalker.nextNode())) {
      const textNode = currentNode as Text;

      // Skip empty text nodes
      if (!textNode.textContent || textNode.textContent.length === 0) {
        continue;
      }

      // Check if this text node is within the range
      if (!this.isNodeInRange(textNode, range)) {
        continue;
      }

      const textLength = textNode.textContent.length;
      let startOffset = 0;
      let endOffset = textLength;

      // Adjust offsets for start node
      if (textNode === range.startContainer) {
        startOffset = range.startOffset;
      }

      // Adjust offsets for end node
      if (textNode === range.endContainer) {
        endOffset = range.endOffset;
      }

      // Only add if there's actual content to wrap
      if (endOffset > startOffset) {
        textNodes.push({ node: textNode, startOffset, endOffset });
      }
    }

    console.log(`Found ${textNodes.length} text node(s) (complex case)`);
    return textNodes;
  }

  /**
   * Check if a node is within a range
   */
  private isNodeInRange(node: Node, range: Range): boolean {
    try {
      const nodeRange = document.createRange();
      nodeRange.selectNode(node);

      // Check if node is completely before the range
      if (range.compareBoundaryPoints(Range.START_TO_END, nodeRange) <= 0) {
        return false;
      }

      // Check if node is completely after the range
      if (range.compareBoundaryPoints(Range.END_TO_START, nodeRange) >= 0) {
        return false;
      }

      return true;
    } catch {
      return false;
    }
  }

  /**
   * Apply annotation styles
   */
  private applyStyle(
    element: HTMLElement,
    style: AnnotationStyle,
    color: AnnotationColor,
    config: (typeof COLORS)[AnnotationColor]
  ): void {
    element.style.cursor = "pointer";
    element.style.transition = "all 0.2s ease";
    element.style.color = "inherit"; // Preserve text color

    if (style === "highlight") {
      element.style.backgroundColor = config.highlight;
      element.style.borderBottom = "none";
    } else {
      // underline
      element.style.backgroundColor = "transparent";
      element.style.borderBottom = `3px solid ${config.underline}`;
      element.style.paddingBottom = "1px";
    }
  }
}

export const engine = new AnnotationEngine();
